import {PreviewFormValues} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {makeZeroIfUndefined} from 'components/customer/Preview3D/helpers';
import {RoomPlannerFormValues} from 'components/customer/RoomPlanner/types';
import {getProductAttributes} from 'components/customer/RoomPlanner/helpers/getProductAttributes';

type FormValues = {
    height: number;
    width: number;
    microwaveOpeningHeight: number;
    shelvesHeightReference: ShelvesTypeReference;
    shelvesHeightReference3Part: ShelvesTypeReference3Part;
    upperShelfOffset: number;
    lowerShelfOffset: number;
};

type ShelvesTypeReference = {
    upper_shelves: number;
    lower_shelves: number;
};

type ShelvesTypeReference3Part = ShelvesTypeReference & {
    middle_shelves: number;
};

export const getMultipartFormValues = (values: PreviewFormValues) => {
    const cabinetUpperPartitionHeight =
        Number(values.cabinet_upper_partition_height) > 1
            ? Number(values.cabinet_upper_partition_height) /
              Number(values.cabinet_height)
            : Number(values.cabinet_upper_partition_height);
    const cabinetLowerPartitionHeight =
        Number(values.cabinet_lower_partition_height) > 1
            ? Number(values.cabinet_lower_partition_height) /
              Number(values.cabinet_height)
            : Number(values.cabinet_lower_partition_height);

    const upperShelfOffset =
        cabinetUpperPartitionHeight * Number(values.cabinet_height);
    const lowerShelfOffset =
        cabinetLowerPartitionHeight * Number(values.cabinet_height);

    return {
        height: makeZeroIfUndefined(values.cabinet_height),
        width: makeZeroIfUndefined(values.cabinet_width),
        microwaveOpeningHeight: makeZeroIfUndefined(
            values.microwave_opening_height
        ),
        shelvesHeightReference: {
            upper_shelves:
                Number(values.cabinet_height) -
                Number(values.cabinet_partition_height),
            lower_shelves: Number(values.cabinet_partition_height),
        },
        shelvesHeightReference3Part: {
            upper_shelves: Number(values.cabinet_height) - upperShelfOffset,
            middle_shelves: upperShelfOffset - lowerShelfOffset,
            lower_shelves: lowerShelfOffset,
        },
        upperShelfOffset,
        lowerShelfOffset,
    };
};

// Overall height of the microwave box: the clear opening plus the exterior
// panels above and below it. 0 when there is no opening.
export const getMicrowaveBoxHeight = (
    openingHeight: number,
    exteriorThickness: number
) => (openingHeight > 0 ? openingHeight + exteriorThickness * 2 : 0);

export const generateMultiPart = (
    values: RoomPlannerFormValues,
    formValues: FormValues,
    variables: TemplateVariable
) => {
    const carcaseThickness = values.cabinet_carc_thickness;
    const exteriorThickness = values.cabinet_ext_thickness;
    const thicknessDiff = exteriorThickness - carcaseThickness;
    // Only the base microwave drawer product frames the opening with exterior
    // panels. The wall oven tower reads microwave.topHeight as the length of
    // its microwave-section side panels, so it must stay equal to the opening.
    const microwaveBoxHeight = getProductAttributes(variables, values)
        .hasMicrowaveDrawer
        ? getMicrowaveBoxHeight(
              formValues.microwaveOpeningHeight,
              exteriorThickness
          )
        : formValues.microwaveOpeningHeight;

    return {
        microwave: {
            bottomHeight: formValues.height - microwaveBoxHeight,
            topHeight: microwaveBoxHeight,
            topWidth: formValues.width - thicknessDiff,
            thicknessDiff,
        },
        pantry: {
            bottomHeight: formValues.shelvesHeightReference.lower_shelves,
            topHeight: formValues.shelvesHeightReference.upper_shelves,
            bottomBaseTopPosition:
                formValues.shelvesHeightReference.lower_shelves -
                carcaseThickness,
            upperSidePanelPosition:
                formValues.shelvesHeightReference.lower_shelves,
        },
        cornerPantry: {
            bottomHeight: formValues.shelvesHeightReference3Part.lower_shelves,
            topHeight: formValues.shelvesHeightReference3Part.upper_shelves,
            middleHeight: formValues.shelvesHeightReference3Part.middle_shelves,
            bottomBaseTopPosition:
                formValues.shelvesHeightReference3Part.lower_shelves -
                carcaseThickness,
            upperSidePanelPosition: formValues.upperShelfOffset,
            middleSidePanelPosition: formValues.lowerShelfOffset,
        },
    };
};
